import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { Auth, apiErrorMessage } from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

/**
 * Dual-purpose dialog:
 *  - Optional mode (open/onClose props) — invoked from TopBar menu.
 *  - Forced mode (no onClose, blocking) — when `mustChangePassword=true`.
 */
export default function ChangePasswordDialog({ open, onClose, forced = false }) {
  const { mustChangePassword, setMustChangePassword, logout } = useApp();
  const isForced = forced || mustChangePassword;
  const visible = isForced || !!open;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (visible) {
      setCurrent(""); setNext(""); setConfirm(""); setError("");
    }
  }, [visible]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!current) return setError("Current password is required");
    if ((next || "").length < 8) return setError("New password must be at least 8 characters");
    if (next !== confirm) return setError("New password and confirmation do not match");
    if (next === current) return setError("New password must differ from the current one");
    setSubmitting(true);
    try {
      await Auth.changePassword({ current_password: current, new_password: next });
      toast.success("Password updated");
      setMustChangePassword(false);
      onClose?.();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to change password"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o) => {
    if (!o && !isForced) onClose?.();
  };

  return (
    <Dialog open={visible} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="change-password-dialog"
        className="sm:max-w-md"
        onPointerDownOutside={(e) => isForced && e.preventDefault()}
        onEscapeKeyDown={(e) => isForced && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isForced && <ShieldAlert className="h-5 w-5 text-amber-600" />}
            {isForced ? "Set a New Password" : "Change Password"}
          </DialogTitle>
          {isForced && (
            <DialogDescription>
              Your password was issued by Admin. Please set a new password to continue.
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current" className="text-xs">Current Password</Label>
            <Input
              id="cp-current" type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current-input" autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new" className="text-xs">New Password</Label>
            <Input
              id="cp-new" type="password" autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new-input"
            />
            <p className="text-[10px] text-slate-500">Minimum 8 characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm" className="text-xs">Confirm New Password</Label>
            <Input
              id="cp-confirm" type="password" autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm-input"
            />
          </div>

          {error && (
            <div
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700"
              data-testid="cp-error"
            >
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {isForced ? (
              <Button
                type="button" variant="outline" onClick={logout}
                data-testid="cp-logout-btn"
              >
                Logout
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            )}
            <Button type="submit" disabled={submitting} data-testid="cp-submit-btn">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
